import Button, { ButtonProps } from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import React, { AnchorHTMLAttributes } from 'react';
import classNames from 'classnames';
import { Link, LinkProps } from 'react-router-dom';
import { SmallSpinner } from 'features/app/components/SmallSpinner';

type StylesProps = {
  isLoading?: boolean;
  color?: string;
  maxWidth?: string | number;
};

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    padding: theme.spacing(1.5, 2.5),
    borderRadius: theme.spacing(1.25),
  },
  fullWidth: {
    width: '100%',
  },
  maxWidth: ({ maxWidth }: StylesProps) => ({
    maxWidth: maxWidth ? maxWidth : 'unset',
  }),
  loading: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'inherit',
  },
  defaultLabel: ({ isLoading, color }: StylesProps) => ({
    visibility: isLoading ? 'hidden' : 'visible',
    fontSize: '18px',
    color: color || theme.palette.common.white,
    textTransform: 'none',
    fontWeight: 'normal',
    [theme.breakpoints.down('md')]: {
      fontSize: '14px',
      lineHeight: '14px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '10px',
      lineHeight: '10px',
    },
  }),
  loader: {
    height: '100%',
    backgroundColor: 'inherit',
  },
  fixedBtn: {
    [theme.breakpoints.down('xs')]: {
      position: 'fixed',
      zIndex: 100,
      right: theme.spacing(2),
      bottom: theme.spacing(2),
      visibility: 'visible',
    },
  },
  linkClasses: {
    textDecoration: 'none',
    outlined: 'none',
  },
  arrow: {
    marginLeft: theme.spacing(12),
    border: 'solid white',
    borderWidth: '0 2px 2px 0',
    display: 'none',
    padding: '4px',
    transform: 'rotate(-45deg)',
  },
}));

type OwnProps = {
  fixedToBottom?: boolean;
  isLoading?: boolean;
  loaderColor?: string;
  fullWidth?: boolean;
  to?: LinkProps['to'];
  href?: HTMLAnchorElement['href'];
  linkProps?: Omit<LinkProps, 'to'>;
  hrefProps?: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;
  size?: 'small' | 'medium' | 'large';
  color?: 'inherit' | 'primary' | 'secondary' | 'default';
  labelClass?: string;
  maxWidth?: number;
};

export type WithLoadingProps = OwnProps & ButtonProps;

const ButtonWithLoading: React.FC<WithLoadingProps> = ({
  children,
  isLoading,
  disabled,
  className,
  fixedToBottom,
  fullWidth,
  to,
  href,
  hrefProps,
  linkProps,
  size = 'medium',
  color = 'primary',
  variant = 'contained',
  labelClass,
  maxWidth,
  ...otherProps
}) => {
  const classes = useStyles({ isLoading, color, maxWidth });

  const renderButton = () => (
    <Button
      size={size}
      variant={variant}
      color={color}
      className={`${fixedToBottom ? classes.fixedBtn : ''}
        ${classNames(
        otherProps.classes?.root ?? classes.root,
        fullWidth && classes.fullWidth,
        maxWidth && classes.maxWidth,
        className
      )}`}
      {...otherProps}
      disabled={disabled || isLoading}
    >
      <SmallSpinner pad="right" show={isLoading} />
      {/* {isLoading && <div className={classes.loading}>Loading...</div>} */}
      <span className={labelClass ?? classes.defaultLabel}>
        {children} <i className={classes.arrow} />
      </span>
    </Button>
  );

  return to ? (
    <Link
      to={to}
      className={classNames(classes.linkClasses, fullWidth && classes.fullWidth, maxWidth && classes.maxWidth)}
      {...linkProps}
    >
      {renderButton()}
    </Link>
  ) : href ? (
    <a
      href={href}
      className={classNames(classes.linkClasses, fullWidth && classes.fullWidth, maxWidth && classes.maxWidth)}
      {...hrefProps}
    >
      {renderButton()}
    </a>
  ) : (
    renderButton()
  );
};

export default ButtonWithLoading;
